import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
  type FilterField,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { donationBudgetHoldersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { getDateFromToFilterFields, getMemberFilterField } from '@/utils/filterFields';
import { mpSummaryInfiniteQueryOptions } from '../api/marketPromotion.api';
import { donationProjectsQueryOptions } from '../api/marketPromotionP2.api';
import { BudgetProjectsDialog } from '../components/BudgetProjectsDialog';
import { getMpSummaryColumns } from '../components/MpColumns';
import { BUDGET_HOLDER_LABEL } from '../constants/marketPromotion.constants';
import { useMpSummaryFilters } from '../hooks/useMpReportFilters';
import type { MpSummaryRow } from '../types/marketPromotion.types';
import { getDistributionSummaryStatCards } from '../utils/budgetStatCards';
import { getDonationProjectFilterField } from '../utils/mpFilterFields';

export function MpDistributionSummaryPage() {
  const controller = useMpSummaryFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(mpSummaryInfiniteQueryOptions(controller.params));
  const projectsQuery = useQuery(donationProjectsQueryOptions());
  const { data: budgetHolders } = useQuery(donationBudgetHoldersQueryOptions());

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [projectsHolder, setProjectsHolder] = useState<MpSummaryRow | null>(null);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);

  function handleOpenProjects(holder: MpSummaryRow) {
    setProjectsHolder(holder);
    setIsProjectsOpen(true);
  }

  const filterFields: FilterField[] = [
    ...getDateFromToFilterFields(),
    getDonationProjectFilterField(projectsQuery.data, projectsQuery.isLoading, {
      isRequired: false,
    }),
    getMemberFilterField({
      key: 'userId',
      label: BUDGET_HOLDER_LABEL,
      placeholder: 'All Budget Holders',
      members: budgetHolders,
    }),
  ];

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Distribution Summary"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: 'Name, HR ID or mobile',
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel fields={filterFields} {...getFilterPanelProps(controller)} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the summary"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            stats={getDistributionSummaryStatCards(firstPageMeta?.totals)}
          />

          <DataTable
            data={rows}
            columns={getMpSummaryColumns(controller.params.search, handleOpenProjects)}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="No budget matches"
            isRowHighlighted={(holder) => holder.userId === projectsHolder?.userId}
            {...loadMoreProps}
          />
        </>
      )}

      <BudgetProjectsDialog
        holder={projectsHolder}
        open={isProjectsOpen}
        onClose={() => setIsProjectsOpen(false)}
      />
    </div>
  );
}
